import { useModel } from '@umijs/max';
import { DefaultOptionType } from 'antd/es/select';
import { useCallback, useMemo } from 'react';

export default (): {
  itemDefsGroupedBySponsorOptions: DefaultOptionType[];
  itemDefsWithSponsorAndLeaderboard: API.Service.ItemDefWithSponsorAndLeaderboard[];
  itemDefsWithSponsorAndLeaderboardLoading: boolean;
  fetchRewardsForSponsor: (
    sponsorId: string,
  ) => Promise<API.Service.ItemDefWithSponsorAndLeaderboard[]>;
  fetchRewardsForSponsorLeaderboard: (
    leaderboardId: string,
  ) => Promise<API.Service.ItemDefWithSponsorAndLeaderboard[]>;
} => {
  const { itemDefs, itemDefsLoading, refreshItemDefs } = useModel('itemDefs');

  const { sponsors, sponsorsLoading } = useModel('sponsors');

  const { sponsorLeaderboards, sponsorLeaderboardsLoading } = useModel(
    'sponsorLeaderboards',
  );

  const itemDefsWithSponsorAndLeaderboardLoading = useMemo(() => {
    return itemDefsLoading || sponsorsLoading || sponsorLeaderboardsLoading;
  }, [itemDefsLoading, sponsorsLoading, sponsorLeaderboardsLoading]);

  const itemDefsGroupedBySponsor = useMemo(() => {
    const otherItemDefs = itemDefs.filter(
      (itemDef) =>
        !sponsors?.some((sponsor) => sponsor.id === itemDef.sponsorId),
    );

    const itemDefsGroupedBySponsor: API.Service.ItemDefGroupedBySponsor[] =
      sponsors?.map((sponsor) => {
        return {
          ...sponsor,
          itemDefs: itemDefs.filter(
            (itemDef) => itemDef.sponsorId === sponsor.id,
          ),
        };
      }) || [];

    itemDefsGroupedBySponsor.push({
      id: 'other',
      name: 'Other',
      itemDefs: otherItemDefs,
    });

    return itemDefsGroupedBySponsor;
  }, [sponsors, itemDefs]);

  const itemDefsGroupedBySponsorOptions = useMemo(() => {
    return itemDefsGroupedBySponsor
      .filter(
        (itemDefGroupedBySponsor) =>
          itemDefGroupedBySponsor.itemDefs?.length > 0,
      )
      .map((itemDefGroupedBySponsor) => ({
        label: itemDefGroupedBySponsor.name,
        //value: itemDefGroupedBySponsor.id,
        options: itemDefGroupedBySponsor.itemDefs.map((itemDef) => ({
          label: itemDef.name,
          value: itemDef.id,
        })),
      }));
  }, [itemDefsGroupedBySponsor]);

  const itemDefsWithSponsorAndLeaderboard = useMemo(() => {
    return itemDefs.map((itemDefs) => ({
      ...itemDefs,
      leaderboard: sponsorLeaderboards.find(
        (sponsorLeaderboard) =>
          sponsorLeaderboard.id === itemDefs.leaderboardId,
      ),
      sponsor: sponsors.find((sponsor) => sponsor.id === itemDefs.sponsorId),
    }));
  }, [itemDefs, sponsorLeaderboards, sponsors]);

  const fetchRewardsForSponsor = useCallback(
    async (sponsorId: string) => {
      const itemDefs = await refreshItemDefs();
      return itemDefs
        .filter(
          (itemDef) =>
            itemDef.sponsorId === sponsorId && !itemDef.leaderboardId,
        )
        .map((itemDef) => ({
          ...itemDef,
          leaderboard: sponsorLeaderboards.find(
            (sponsorLeaderboard) =>
              sponsorLeaderboard.id === itemDef.leaderboardId,
          ),
          sponsor: sponsors.find((sponsor) => sponsor.id === itemDef.sponsorId),
        }))
        .sort((a, b) => (a.value ?? 0) - (b.value ?? 0));
    },
    [sponsorLeaderboards, sponsors, refreshItemDefs],
  );

  const fetchRewardsForSponsorLeaderboard = useCallback(
    async (leaderboardId: string) => {
      const itemDefs = await refreshItemDefs();
      return itemDefs
        .filter((itemDef) => itemDef.leaderboardId === leaderboardId)
        .map((itemDef) => ({
          ...itemDef,
          leaderboard: sponsorLeaderboards.find(
            (sponsorLeaderboard) =>
              sponsorLeaderboard.id === itemDef.leaderboardId,
          ),
        }))
        .sort((a, b) => (a.value ?? 0) - (b.value ?? 0));
    },
    [sponsorLeaderboards, refreshItemDefs],
  );

  return {
    itemDefsGroupedBySponsorOptions,
    itemDefsWithSponsorAndLeaderboard,
    itemDefsWithSponsorAndLeaderboardLoading,
    fetchRewardsForSponsor,
    fetchRewardsForSponsorLeaderboard,
  };
};
